// pages/ChatSpace/ChatSpaceCitations.js
import React from 'react';
import {
    Box,
    Typography,
    IconButton,
    Link,
    Chip,
    Divider,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { marked } from 'marked';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    textTertiary: '#94a3b8',
    accent: '#3b82f6',
};

const CitationItem = ({ citation }) => {
    const getHtml = (content) => {
        try {
            const extractSummary = (text) => {
                const match = text.match(/<source_page_summary>([\s\S]*?)<\/source_page_summary>/);
                return match ? match[1].trim() : citation.summary;
            };
            const contentToParse = extractSummary(content || citation.summary);
            return marked.parse(contentToParse || 'No summary available.');
        } catch {
            return citation.summary || 'No summary available.';
        }
    };

    return (
        <Box
            sx={{
                border: '1px solid rgba(0,0,0,0.06)',
                borderRadius: '10px',
                p: 1.5,
                mb: 1.5,
                bgcolor: 'rgba(248,250,252,0.6)',
                transition: 'all 0.2s ease',
                '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.9)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    transform: 'translateY(-1px)',
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Link
                    href={citation.url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontFamily, fontSize: '0.75rem', fontWeight: 600, flex: 1, lineHeight: 1.3, color: colors.accent }}
                    underline="hover"
                >
                    {citation.title_tag || 'Source Document'}
                </Link>
                <OpenInNewIcon sx={{ fontSize: 14, color: colors.textTertiary, flexShrink: 0, mt: 0.25 }} />
            </Box>
            <Chip
                label={(citation.data_source || '').replace('_', ' ')}
                size="small"
                sx={{
                    fontFamily,
                    fontSize: '0.65rem',
                    height: 20,
                    mb: 1,
                    bgcolor: 'rgba(59,130,246,0.1)',
                    color: colors.accent,
                    border: 'none',
                }}
            />
            <Divider sx={{ mb: 1, borderColor: 'rgba(0,0,0,0.04)' }} />
            <Box
                sx={{
                    fontFamily,
                    fontSize: '0.75rem',
                    color: colors.textSecondary,
                    '& p': { m: 0 },
                    '& a': { color: colors.accent },
                    overflow: 'hidden',
                    maxHeight: 120,
                }}
                dangerouslySetInnerHTML={{ __html: getHtml(citation.page_content) }}
            />
        </Box>
    );
};

const CollapsedCitationItem = ({ citation, index }) => (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
        <IconButton
            size="small"
            onClick={() => window.open(citation.url, '_blank', 'noopener,noreferrer')}
            title={citation.title_tag}
            sx={{ width: 36, height: 36 }}
        >
            <Box
                sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: 'rgba(59,130,246,0.1)',
                    color: colors.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                }}
            >
                {index + 1}
            </Box>
        </IconButton>
    </Box>
);

export default function ChatSpaceCitations({ isOpen, isCollapsed = false, onToggle, onClose, citations = [] }) {
    if (!isOpen) return null;

    return (
        <>
            {/* Mobile overlay */}
            <Box
                onClick={onClose}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    position: 'fixed',
                    inset: 0,
                    bgcolor: 'rgba(0,0,0,0.4)',
                    backdropFilter: 'blur(4px)',
                    zIndex: 40,
                }}
            />

            <Box
                sx={{
                    position: { xs: 'fixed', lg: 'relative' },
                    top: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: { xs: 50, lg: 'auto' },
                    width: { xs: 280, lg: '100%' },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    bgcolor: { xs: 'rgba(255,255,255,0.95)', lg: 'transparent' },
                    backdropFilter: { xs: 'blur(12px)', lg: 'none' },
                    boxShadow: { xs: '0 8px 32px rgba(0,0,0,0.12)', lg: 0 },
                    borderRadius: { xs: '14px 0 0 14px', lg: 0 },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isCollapsed ? 'center' : 'space-between',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        px: isCollapsed ? 1 : 2,
                        py: 1.5,
                        flexShrink: 0,
                    }}
                >
                    {!isCollapsed ? (
                        <Box>
                            <Typography sx={{ fontFamily, fontSize: '0.875rem', fontWeight: 700, color: colors.textPrimary }}>
                                Sources
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: colors.textTertiary }}>
                                {citations.length} source{citations.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center' }}>
                            <MenuBookIcon sx={{ color: colors.accent, fontSize: 20 }} />
                            <Typography sx={{ fontFamily, fontSize: '0.65rem', color: colors.textTertiary }}>
                                {citations.length}
                            </Typography>
                        </Box>
                    )}
                    {!isCollapsed && onToggle && (
                        <IconButton
                            size="small"
                            onClick={onToggle}
                            title="Collapse"
                            sx={{
                                color: colors.textTertiary,
                                '&:hover': { bgcolor: 'rgba(59,130,246,0.1)', color: colors.accent },
                            }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {/* Content */}
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: isCollapsed ? 0.5 : 1.5,
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(0,0,0,0.1)',
                        borderRadius: '4px',
                    },
                }}>
                    {citations.length > 0 ? (
                        isCollapsed
                            ? citations.map((c, i) => <CollapsedCitationItem key={i} citation={c} index={i} />)
                            : citations.map((c, i) => <CitationItem key={i} citation={c} />)
                    ) : (
                        !isCollapsed && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                <OpenInNewIcon sx={{ fontSize: 40, color: 'rgba(0,0,0,0.08)', mb: 1 }} />
                                <Typography sx={{ fontFamily, fontSize: '0.8rem', color: colors.textTertiary }}>No sources available</Typography>
                            </Box>
                        )
                    )}
                </Box>
            </Box>
        </>
    );
}
